import React from 'react'
import {
  Box,
  Card,
  Columns,
  Container,
  Element,
  Heading,
  Level,
  Section,
  Tag,
} from 'react-bulma-components'
import { useParams } from 'react-router-dom'
import Layout from '../../components/layout/layout'
import Error404 from '../../components/errors/404'
import Breadcrumbs from '../../components/breadcrumbs/breadcrumbs'
import { WorkPeriodDatesForm } from '../../components/work-periods/forms/work-period-dates-form'
import WorkPeriodMessages from '../../components/work-periods/work-period-messages'
import WorkPeriodActions from '../../components/work-periods/work-period-actions'
import { Link } from 'react-router-dom'
import WorkPeriodPageTitle from '../../components/work-periods/titles/work-period-page-title'
import { WorkPeriodPageNavigation } from '../../components/work-periods/pages/work-period-page-navigation'
import WorkPeriodEventsForm from '../../components/work-periods/work-period-events-form'
import Protected, { isAuthorizedFor } from '../../components/protected/protected'
import Modal, { useModal } from '../../components/modal'
import { useAddTagsToWorkPeriodsMutation, useWorkPeriodQuery } from '../../queries/work-periods'
import ClockingRulesDescription from '../../components/clocking-rules/clocking-rules-description'
import ClockingRulesBreakDescription from '../../components/clocking-rules/clocking-rules-break-description'
import WorkPeriodClockings from '../../components/work-periods/work-period-clockings'
import WorkPeriodServiceForm from '../../components/work-periods/work-period-service-form'
import WorkPeriodTags from '../../components/work-periods/work-period-tags'
import { useUpdateTagsMutation } from '../../queries/work-periods'
import { useMissionQuery } from '../../queries/missions'
import { WorkPeriodWithSummary } from '../../api/work-periods'
import { getDocIdAsString } from '../../utils/documents'
import { useWorkPeriodNavPrevNext } from '../../hooks/use-work-period-nav-prev-next'

const WorkPeriodDetails: React.FunctionComponent = () => {
  //Get mission and work period
  const { missionId, workPeriodId } = useParams()
  const { data: workPeriod, status } = useWorkPeriodQuery(workPeriodId, { summary: true })
  const { data: mission } = useMissionQuery(missionId)
  const editServiceModal = useModal()
  const updateTagsMutation = useUpdateTagsMutation()
  const addTagsToWorkPeriodsMutation = useAddTagsToWorkPeriodsMutation()
  const [showClockingRules, setShowClockingRules] = React.useState(false)

  const { previousWorkPeriod, nextWorkPeriod } = useWorkPeriodNavPrevNext(
    missionId,
    workPeriod ?? undefined,
    workPeriodId,
  )

  const breadcrumbs = React.useMemo(
    () => [
      {
        label: 'Récapitulatif',
        to: '/summary',
      },
      {
        label: 'Journée de travail',
      },
    ],
    [],
  )

  if (!workPeriod && !mission && status !== 'loading') return <Error404 />
  //TODO: Suspense?
  else if (!workPeriod || !mission) return <></>

  return (
    <Layout>
      <Section>
        <Container breakpoint="widescreen" max={true}>
          <Level>
            <Level.Side>
              <Breadcrumbs items={breadcrumbs} />
            </Level.Side>
            <Level.Side style={{ minWidth: 250 }} display="block" align="right">
              <WorkPeriodPageNavigation
                missionId={missionId as string}
                previousWorkPeriod={previousWorkPeriod}
                nextWorkPeriod={nextWorkPeriod}
              />
            </Level.Side>
          </Level>
          <Level alignItems="flex-start" mb={6}>
            <Level.Side>
              <Element>
                <WorkPeriodPageTitle
                  workPeriod={workPeriod}
                  onServiceEdit={
                    isAuthorizedFor([
                      {
                        role: 'employerMember',
                        action: { resource: 'missions', name: 'updateWorkPeriod' },
                      },
                    ]) && workPeriod.__actions.canBeUpdated
                      ? () => {
                          editServiceModal.setIsDisplayed(true)
                        }
                      : undefined
                  }
                />
              </Element>
              <Protected
                roles={['employerMember']}
                action={{ resource: 'missions', name: 'updateWorkPeriod' }}
              >
                {workPeriodId && missionId && (
                  <Modal
                    actions={editServiceModal}
                    title="Modifier l'affectation de la journée de travail"
                    dataTest="work-period-edit-service-modal"
                  >
                    <Element>
                      <Element mb={2}>
                        Vous pouvez modifier l'affectation de la journée de travail. <br />
                        L'affectation liée à la mission et aux autres journées de travail de la
                        mission seront conservées.
                      </Element>
                      <WorkPeriodServiceForm workPeriod={workPeriod} />
                    </Element>
                  </Modal>
                )}
              </Protected>
            </Level.Side>
            <Level.Side align="right">
              <Tag.Group>
                <WorkPeriodMessages workPeriod={workPeriod} type="tag" />
              </Tag.Group>
            </Level.Side>
          </Level>
          {mission && workPeriod && (
            <Element mb={5}>
              <Heading size={4}>Horaire de la Journée de Travail</Heading>
              <Card mb={2}>
                <Card.Content>
                  <WorkPeriodDatesForm
                    workPeriod={workPeriod as WorkPeriodWithSummary}
                    disabled={!workPeriod.__actions.canBeManualClocked}
                  />
                </Card.Content>
              </Card>
              <Element textAlign={'right'} mt={2}>
                <Link to={`/missions/${getDocIdAsString(workPeriod.mission)}`}>
                  Voir la mission
                </Link>
              </Element>
              <Protected roles={['employerMember']}>
                <Heading size={4} mt={2} mb={1}>
                  Catégories
                </Heading>
                <Box mt={2}>
                  <WorkPeriodTags
                    tags={workPeriod?.tags || []}
                    formProps={{
                      onSubmit: async params => {
                        addTagsToWorkPeriodsMutation.mutate({
                          missionId: getDocIdAsString(workPeriod.mission),
                          from: params.form,
                          to: params.to,
                          tags: params.tags.map(tag => tag._id),
                        })
                      },
                      onCancel: () => {},
                      defaultFormDate: new Date(workPeriod.start.date),
                      defaultToDate: new Date(workPeriod.end.date),
                      minDate: new Date(workPeriod.mission.start),
                      maxDate: workPeriod.mission.end
                        ? new Date(workPeriod.mission.end)
                        : undefined,
                      dateRange: true,
                    }}
                    isEditable={workPeriod.__actions.canBeUpdated}
                    onTagDelete={tagId => {
                      if (workPeriod) {
                        const currentTagIds = workPeriod.tags?.map(tag => tag._id) || []
                        const updatedTagIds = currentTagIds.filter(id => id !== tagId)
                        updateTagsMutation.mutate({
                          workPeriodId: workPeriod._id,
                          tags: updatedTagIds,
                        })
                      }
                    }}
                  />
                </Box>
              </Protected>
              <Protected
                roles={['employerMember']}
                action={{ resource: 'missions', name: 'updateWorkPeriodEvents' }}
              >
                <Element mb={5}>
                  <Heading size={4}>Évènements</Heading>
                  <Card>
                    <Card.Content>
                      <WorkPeriodEventsForm
                        mission={mission}
                        workPeriod={workPeriod}
                        disabled={!workPeriod.__actions.canBeUpdated}
                      />
                    </Card.Content>
                  </Card>
                </Element>
              </Protected>
              <Heading size={4} mt={2}>
                Pointages
              </Heading>
              <Card mb={2}>
                <Card.Content>
                  <Element>
                    {workPeriod.start.clocking ? (
                      <WorkPeriodClockings workPeriod={workPeriod} />
                    ) : (
                      <Element textColor="grey">Aucun pointage</Element>
                    )}
                  </Element>

                  {showClockingRules && (
                    <Element mt={3}>
                      <Element renderAs="hr" mb={4} />
                      <Columns>
                        <Columns.Column size={6}>
                          <Heading size={6} mb={1}>
                            Entrée
                          </Heading>
                          <ClockingRulesDescription
                            clockingRules={workPeriod.clockingRules}
                            type="start"
                          />
                        </Columns.Column>
                        <Columns.Column size={6}>
                          <Heading size={6} mb={1}>
                            Sortie
                          </Heading>
                          <ClockingRulesDescription
                            clockingRules={workPeriod.clockingRules}
                            type="end"
                          />
                        </Columns.Column>
                        <Columns.Column>
                          <Heading size={6} mb={1}>
                            Pauses
                          </Heading>
                          <ClockingRulesBreakDescription clockingRules={workPeriod.clockingRules} />
                        </Columns.Column>
                      </Columns>
                    </Element>
                  )}
                </Card.Content>
              </Card>

              <Element
                textAlign={'right'}
                style={{ cursor: 'pointer' }}
                textColor="link"
                data-test="show-clocking-rules-button"
                onClick={() => {
                  setShowClockingRules(!showClockingRules)
                }}
              >
                {showClockingRules
                  ? 'Masquer la règle de pointage'
                  : 'Afficher la règle de pointage'}
              </Element>
            </Element>
          )}
          <Element mt={5}>
            <Heading size={4}>Actions</Heading>
            <Card>
              <Card.Content>
                <Element>
                  <WorkPeriodActions mission={mission} workPeriod={workPeriod} />
                </Element>
              </Card.Content>
            </Card>
          </Element>
        </Container>
      </Section>
    </Layout>
  )
}

export default WorkPeriodDetails
